/* eslint-disable react/forbid-prop-types */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FiPlus, FiMinus } from 'react-icons/fi';
import { formatPrice } from '../../utils/onlineVisaUtils';
import './FareSummary.scss';

const pad = (n) => String(n).padStart(2, '0');

function computeBreakdown(paxKey, visa) {
  // Preferred path: the backend surfaces a per-pax breakdown, e.g.
  //   visa.visaFare.paxBreakup = { ADULT: { embassyFees, tax, marketPlaceFee, ... } }
  const paxBreakup = visa.visaFare.paxWisePriceList.find(
    (x) => x.paxType === paxKey,
  );
  if (paxBreakup) {
    return {
      baseFare: Number(paxBreakup.paxTotalAmount || 0),
      tax: Number(paxBreakup.PaxHandleCharges + paxBreakup.PaxTfeeInv || 0),
      handlingCharges: Number(
        paxBreakup.PaxHiddenMarkupOnBaseFare + paxBreakup.hiddenMarkup || 0,
      ),
      comission: Number(paxBreakup.paxCommission || 0),
      tds: Number(paxBreakup.paxTds || 0),
      totalNet: Number(paxBreakup.PaxInvoiceTotal || 0),
      totalFareQuote: Number(paxBreakup.PaxFareQuoteTotal || 0),
      perPax: Number(paxBreakup.PaxFareQuoteTotal / paxBreakup.paxCount || 0),
    };
  }
  return null;
}

function FareSummary({ visa, paxCount, childAges, infantAges, currency }) {
  const { adults = 0, children = 0, infants = 0 } = paxCount || {};
  const totalPax = adults + children + infants;
  const cur = currency || visa?.visaFare?.currency || 'INR';

  const rows = [
    {
      key: 'ADT',
      label: 'Adult',
      count: adults,
      ages: null,
    },
    {
      key: 'CHD',
      label: 'Child',
      count: children,
      ages: childAges,
    },
    {
      key: 'INF',
      label: 'Infant',
      count: infants,
      ages: infantAges,
    },
  ].filter((r) => r.count > 0);

  const [expanded, setExpanded] = useState({});
  const toggleExpanded = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const formatAgesText = (ages) => {
    if (!Array.isArray(ages) || ages.length === 0) return '';
    const list = ages
      .map((a) => (a === '' || a == null ? null : a))
      .filter((a) => a !== null);
    if (list.length === 0) return '';
    return `age - ${list.join(', ')}`;
  };

  return (
    <aside className="fare-summary">
      <h3 className="fare-summary__title">Fare Summary</h3>
      <span className="fare-summary__title-underline" aria-hidden />

      <div className="fare-summary__pax-row">
        <span className="fare-summary__pax-label">No. of Travelers</span>
        <span className="fare-summary__pax-total">{pad(totalPax)}</span>
      </div>

      <div className="fare-summary__rows">
        {rows.length === 0 && (
          <div className="fare-summary__empty">
            Select travellers to see the fare breakdown.
          </div>
        )}
        {rows.map((r) => {
          const isOpen = !!expanded[r.key];
          const breakdown = computeBreakdown(r.key, visa);
          const agesText = formatAgesText(r.ages);
          return (
            <div
              className={`fare-summary__row ${
                isOpen ? 'fare-summary__row--open' : ''
              }`}
              key={r.key}
            >
              <button
                type="button"
                className="fare-summary__row-head"
                onClick={() => toggleExpanded(r.key)}
                aria-expanded={isOpen}
              >
                <div className="fare-summary__row-left">
                  <span className="fare-summary__expand" aria-hidden>
                    {isOpen ? <FiMinus /> : <FiPlus />}
                  </span>
                  <span className="fare-summary__row-label">
                    <span className="fare-summary__row-label-main">
                      {r.label} * {r.count}
                    </span>
                    {agesText && (
                      <span className="fare-summary__row-label-sub">
                        {agesText}
                      </span>
                    )}
                  </span>
                </div>
                <div className="fare-summary__row-right">
                  <span className="fare-summary__row-amount">
                    {formatPrice(breakdown.totalFareQuote, cur) ||
                      `${cur} ${breakdown.totalFareQuote}`}
                  </span>
                  <span className="fare-summary__row-breakdown">
                    (
                    {formatPrice(breakdown.perPax, cur) ||
                      `${cur} ${breakdown.perPax}`}{' '}
                    * {r.count})
                  </span>
                </div>
              </button>

              {isOpen && (
                <ul className="fare-summary__breakup">
                  <li className="fare-summary__breakup-row">
                    <span>Base Fare</span>
                    <span>
                      {formatPrice(breakdown.baseFare, cur) ||
                        `${cur} ${breakdown.baseFare}`}
                    </span>
                  </li>
                  <li className="fare-summary__breakup-row">
                    <span>Tax</span>
                    <span>
                      {formatPrice(breakdown.tax, cur) ||
                        `${cur} ${breakdown.tax}`}
                    </span>
                  </li>
                  <li className="fare-summary__breakup-row">
                    <span>Handling Charges</span>
                    <span>
                      {formatPrice(breakdown.handlingCharges, cur) ||
                        `${cur} ${breakdown.handlingCharges}`}
                    </span>
                  </li>
                  <li className="fare-summary__breakup-row">
                    <span>Commission</span>
                    <span>
                      {formatPrice(breakdown.comission, cur) ||
                        `${cur} ${breakdown.comission}`}
                    </span>
                  </li>
                  <li className="fare-summary__breakup-row">
                    <span>TDS</span>
                    <span>
                      {formatPrice(breakdown.tds, cur) ||
                        `${cur} ${breakdown.tds}`}
                    </span>
                  </li>
                  <li className="fare-summary__breakup-row">
                    <span>Net Amount</span>
                    <span>
                      {formatPrice(breakdown.totalNet, cur) ||
                        `${cur} ${breakdown.totalNet}`}
                    </span>
                  </li>
                  <li className="fare-summary__breakup-row">
                    <span>Total Amount</span>
                    <span>
                      {formatPrice(breakdown.totalFareQuote, cur) ||
                        `${cur} ${breakdown.totalFareQuote}`}
                    </span>
                  </li>
                </ul>
              )}
            </div>
          );
        })}
      </div>

      <div className="fare-summary__divider" />

      <div className="fare-summary__total">
        <span>Total Cost</span>
        <span>
          {formatPrice(visa.visaFare.fareQuoteTotal, cur) ||
            `${cur} ${visa.visaFare.fareQuoteTotal}`}
        </span>
      </div>
    </aside>
  );
}

FareSummary.propTypes = {
  visa: PropTypes.object,
  paxCount: PropTypes.shape({
    adults: PropTypes.number,
    children: PropTypes.number,
    infants: PropTypes.number,
  }),
  childAges: PropTypes.array,
  infantAges: PropTypes.array,
  currency: PropTypes.string,
};

FareSummary.defaultProps = {
  visa: null,
  paxCount: { adults: 0, children: 0, infants: 0 },
  childAges: [],
  infantAges: [],
  currency: '',
};

export default FareSummary;
